/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext } from 'react';
import { map, isEqual, isNil, omit } from 'lodash';
import { Header, LinkButton, LoneIconButton } from '@blend/enterprise-components';
import { inputFormatters } from '@blend/shared-react';
import { StyleSheet, css } from 'aphrodite';
import { FeesContext } from '../containers/FeesTab';
import { BASE_AGGREGATION_CONFIGS } from '../constants';
import { FeeFormValue, FormSubsectionConfig } from '../types';
import { LedgerSubsection } from './LedgerSubsection/LedgerSubsection';
import { generateLedgerSubsectionTableHeader, generateLedgerSubsectionRow } from '../utils/inputUtils';
import {
  generateLedgerSubsectionRowActionConfigs,
  aggregateFeesBySubsection,
  getFeeIndexById,
  isFeeApprovedAndDeleted,
} from '../utils/closingFeesUtils';
import { Pricing } from 'blb-types/pricing';
import { PendingFeeNotice } from './LedgerSubsection/PendingFeeNotice';

const styles = StyleSheet.create({
  cancelWrapper: {
    marginLeft: 16,
  },
});

interface ClosingFeesLedgerSubsectionProps {
  config: FormSubsectionConfig;
}

const subsectionHeaderSize = 4;

export const ClosingFeesLedgerSubsection: React.FC<ClosingFeesLedgerSubsectionProps> = (
  props: ClosingFeesLedgerSubsectionProps,
) => {
  const {
    formInitialValues,
    formValues,
    setFormValues,
    setCurrentRequest,
    actionsEnabled,
    setCancelRequest,
  } = useContext(FeesContext);
  const { columnConfigs, headerConfigs, hideTableAction, name: subsectionKey } = props.config;

  const initialSubsectionValue = formInitialValues[subsectionKey] || [];
  const subsectionValue = formValues[subsectionKey] || [];
  const tableHeader = generateLedgerSubsectionTableHeader(columnConfigs);

  const getTableAction = () => {
    return (
      <LinkButton
        icon="add"
        label="Add fee"
        onClick={() => {
          // rowIdx is set to -1 since we're not editing a fee line item,
          // but appending to the current list of fees
          setCurrentRequest({
            displayModal: true,
            rowIdx: -1,
            subsectionKey,
            type: 'add',
          });
        }}
      />
    );
  };

  const removeRequestFromState = (rowValue: FeeFormValue, currentFeeIndex: number, initialFeeIndex: number) => {
    const updatedSubsectionValue =
      rowValue.updateType === Pricing.UpdateType.ADD
        ? subsectionValue.filter((_val, idx) => idx !== currentFeeIndex)
        : map(subsectionValue, (val, idx) =>
            idx === currentFeeIndex ? omit(initialSubsectionValue[initialFeeIndex], 'isRevertedFee') : val,
          );
    setFormValues({
      ...formValues,
      ...{ [subsectionKey]: updatedSubsectionValue },
    });
  };

  const renderAggregation = () => {
    const subsectionAggregate = aggregateFeesBySubsection(formValues, [BASE_AGGREGATION_CONFIGS[subsectionKey]]);

    return (
      <Header fontWeight="BOLD" size={subsectionHeaderSize}>
        {inputFormatters.MONEY_CENTS.formatWithDollarSignAndCents(subsectionAggregate)}
      </Header>
    );
  };

  const columnWidths = map(columnConfigs, ({ columnWidth }) => columnWidth);
  const ledgerRowConfigs = map(subsectionValue, (rowValue: FeeFormValue, rowIdx: number) => {
    const cancelRequest = () => {
      const { status, id, updateType } = rowValue;
      // You cannot invoke cancelRequest on Approved fees or on fees without an updateType
      // safeguarding just in case
      if (isNil(status) || isNil(updateType) || status === Pricing.FeeStatus.APPROVED) return false;

      // If fee has no associated id, then it has been generated locally
      if (isNil(id)) {
        return removeRequestFromState(rowValue, rowIdx, rowIdx);
      }

      // If initial and current values are the same, this means that the
      // current pending request came back from Pricing
      const currentFeeIndex = getFeeIndexById(subsectionValue, id);
      const initialFeeIndex = getFeeIndexById(initialSubsectionValue, id);
      if (isEqual(initialSubsectionValue[initialFeeIndex], subsectionValue[currentFeeIndex])) {
        setCancelRequest({
          feeId: id,
          subsectionKey,
          updateType,
        });
      } else {
        removeRequestFromState(rowValue, currentFeeIndex, initialFeeIndex);
      }
    };

    return {
      banner: !isNil(rowValue.updateType) && rowValue.status !== Pricing.FeeStatus.APPROVED && (
        <PendingFeeNotice cancelRequest={cancelRequest} fee={rowValue} updateType={rowValue.updateType} />
      ),
      columns: generateLedgerSubsectionRow(columnConfigs, subsectionKey, rowIdx, formValues),
      disableRowActions: rowValue.status !== Pricing.FeeStatus.APPROVED || !actionsEnabled,
      hideRow: isFeeApprovedAndDeleted(rowValue),
      overwritingAction: rowValue.status === Pricing.FeeStatus.IN_PROGRESS &&
        rowValue.updateType === Pricing.UpdateType.ADD && (
          <div className={css(styles.cancelWrapper)}>
            <LoneIconButton
              btnSize="xs"
              disabled={!actionsEnabled}
              icon="clear"
              isCircle
              onClick={cancelRequest}
              theme="tertiary"
              type="button"
            />
          </div>
        ),
    };
  });

  const ledgerRowActionsConfig = generateLedgerSubsectionRowActionConfigs(
    [[{ type: 'Propose Delete' }, { type: 'Propose Edit' }]],
    subsectionKey,
    setCurrentRequest,
  );

  return (
    <LedgerSubsection
      {...(headerConfigs && {
        headerConfig: {
          rightComponent: renderAggregation(),
          size: subsectionHeaderSize,
          title: headerConfigs.title,
        },
      })}
      columnWidths={columnWidths}
      ledgerHeaderComponents={tableHeader}
      ledgerRowActionsConfig={ledgerRowActionsConfig}
      ledgerRowConfigs={ledgerRowConfigs}
      {...(!hideTableAction && actionsEnabled && { ledgerActions: [getTableAction()] })}
    />
  );
};
